import React, { useEffect, Suspense } from 'react';
import { createPortal } from 'react-dom';
import AppLoader from '../AppLoader/AppLoader';
import useMapAreaPortalTarget from '../../hooks/useMapAreaPortalTarget';
import './DiamondMapModal.css';

/**
 * DiamondMapModal — bespoke map-viewport modal for diamond teaching nodes.
 *
 * Portals into `.deck-map-area` like MapCombatPanel, but stays minimal on
 * purpose: just a backdrop + panel + close button. The DiamondSession
 * component rendered as children owns its own header, step indicator,
 * instruction strip, content area, and bottom bar — all with the purple
 * diamond-accent styling that is the point of this modal.
 *
 * Scoped class prefix: `.dmm-*` so DiamondSession's `.diamond-*` rules
 * coexist cleanly.
 */
export default function DiamondMapModal({ node, onClose, children }) {
  useEffect(() => {
    if (!node) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [node, onClose]);

  // Stabilized portal host — see useMapAreaPortalTarget (prevents re-parent flash).
  const target = useMapAreaPortalTarget(!!node);

  if (!node) return null;

  const panel = (
    <div className="dmm-viewport">
      <div
        className="dmm-overlay"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={`Teaching card: ${node.label || node.short_label || 'Diamond'}`}
      >
        <div className="dmm-body" onClick={(e) => e.stopPropagation()}>
          <button className="dmm-close" onClick={onClose} aria-label="Close teaching card">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="dmm-content">
            <Suspense fallback={
              <div className="app-loader-shell app-loader-shell--panel">
                <AppLoader label="Loading lesson..." size="md" />
              </div>
            }>
              {children}
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );

  if (!target) return null;
  return createPortal(panel, target);
}
